import React, { useState, useRef } from 'react';
import { taxEngineAPI } from '../../services/api';
import { saveBlob, money } from '../../pages/tax/taxWorkspace';
import './ExcelHouseholdTaxImportModal.css';

export default function ExcelHouseholdTaxImportModal({
  isOpen,
  onClose,
  tenantSlug,
  onImportSuccess,
}) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [validationData, setValidationData] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [filterTab, setFilterTab] = useState('ALL'); // 'ALL' | 'VALID' | 'ERRORS'
  const [errorMessage, setErrorMessage] = useState('');
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const handleDownloadTemplate = async () => {
    try {
      setDownloading(true);
      setErrorMessage('');
      const response = await taxEngineAPI.downloadImportTemplate(tenantSlug);
      saveBlob(response.data, 'Households_Tax_Import_Template.xlsx');
    } catch (err) {
      console.error('Failed to download template:', err);
      setErrorMessage(
        err.response?.data?.error || 'टेम्पलेट डाऊनलोड करताना त्रुटी आली. कृपया पुन्हा प्रयत्न करा.'
      );
    } finally {
      setDownloading(false);
    }
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    const name = file.name.toLowerCase();
    if (!name.endsWith('.xlsx') && !name.endsWith('.xls')) {
      setErrorMessage('कृपया फक्त वैध एक्सेल फाईल (.xlsx किंवा .xls) निवडा.');
      return;
    }
    setSelectedFile(file);
    setValidationData(null);
    setImportResult(null);
    setErrorMessage('');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleValidate = async () => {
    if (!selectedFile) {
      setErrorMessage('कृपया तपासणीसाठी प्रथम एक्सेल फाईल निवडा.');
      return;
    }
    try {
      setValidating(true);
      setErrorMessage('');
      const formData = new FormData();
      formData.append('file', selectedFile);
      const res = await taxEngineAPI.importHouseholdTaxExcel(tenantSlug, formData, true);
      setValidationData(res.data);
    } catch (err) {
      console.error('Validation failed:', err);
      setErrorMessage(
        err.response?.data?.error || 'एक्सेल डेटा तपासताना त्रुटी आली. कृपया फाईलचे स्वरूप तपासा.'
      );
    } finally {
      setValidating(false);
    }
  };

  const handleFinalImport = async () => {
    if (!selectedFile) return;
    try {
      setImporting(true);
      setErrorMessage('');
      const formData = new FormData();
      formData.append('file', selectedFile);
      const res = await taxEngineAPI.importHouseholdTaxExcel(tenantSlug, formData, false);
      setImportResult(res.data);
      if (onImportSuccess) {
        onImportSuccess();
      }
    } catch (err) {
      console.error('Final import failed:', err);
      setErrorMessage(
        err.response?.data?.error || 'अंतिम डेटा आयात करताना त्रुटी आली. कृपया पुन्हा प्रयत्न करा.'
      );
    } finally {
      setImporting(false);
    }
  };

  const resetAll = () => {
    setSelectedFile(null);
    setValidationData(null);
    setImportResult(null);
    setErrorMessage('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const filteredRows = (validationData?.rows || []).filter((r) => {
    if (filterTab === 'VALID') return r.isValid;
    if (filterTab === 'ERRORS') return !r.isValid;
    return true;
  });

  return (
    <div
      className="gp-import-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget && !importing) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="gp-import-modal-title"
    >
      <div className="gp-import-modal">
        {/* Header */}
        <div className="gp-import-modal-header">
          <div className="gp-import-modal-header-info">
            <div className="gp-import-header-icon">📊</div>
            <div>
              <h3 id="gp-import-modal-title">घरे, नागरिक, मालमत्ता व कर मागणी एकत्रित आयात (Excel Import)</h3>
              <p>अधिकृत एक्सेल (.xlsx) शीटद्वारे कुटुंबांची नोंद, मालमत्ता व चालू/थकीत करांची निर्मिती</p>
            </div>
          </div>
          <button
            type="button"
            className="gp-import-modal-close-btn"
            onClick={onClose}
            disabled={importing}
            title="बंद करा"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="gp-import-modal-body">
          {errorMessage && (
            <div className="gp-error-note" style={{ background: '#fee2e2', padding: '0.85rem 1.25rem', borderRadius: '0.5rem', width: '100%', maxWidth: '100%', fontSize: '0.9rem', border: '1px solid #f87171' }}>
              ⚠️ {errorMessage}
            </div>
          )}

          {/* Stepper Cards */}
          <div className="gp-import-steps-row">
            <div className="gp-import-step-card">
              <div className="gp-import-step-card-header">
                <span className="gp-step-badge">१</span>
                <h4>नमुना टेम्पलेट डाऊनलोड करा</h4>
              </div>
              <p>प्रमाणित मराठी कॉलम्स, ड्रॉपडाऊन सूची व स्वयंचलित सूत्रे असलेले एक्सेल टेम्पलेट मिळवा.</p>
              <button
                type="button"
                className="gp-template-download-btn"
                onClick={handleDownloadTemplate}
                disabled={downloading}
              >
                📥 {downloading ? 'डाऊनलोड होत आहे…' : 'टेम्पलेट डाऊनलोड (.xlsx)'}
              </button>
            </div>

            <div className={`gp-import-step-card ${selectedFile ? 'active' : ''}`}>
              <div className="gp-import-step-card-header">
                <span className="gp-step-badge">२</span>
                <h4>फाईल भरा व निवडा</h4>
              </div>
              <p>घर क्रमांक, करदात्याचे नाव, मोबाईल, क्षेत्रफळ, बांधकाम प्रकार व करांच्या रकमा भरा.</p>
              {selectedFile && (
                <div className="gp-file-selected-badge" style={{ marginTop: '0.5rem' }}>
                  <span>📄 {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)</span>
                  <button type="button" className="gp-file-remove-btn" onClick={resetAll} title="काढून टाका">✕</button>
                </div>
              )}
            </div>

            <div className={`gp-import-step-card ${validationData ? 'active' : ''}`}>
              <div className="gp-import-step-card-header">
                <span className="gp-step-badge">३</span>
                <h4>तपासा व अंतिम आयात करा</h4>
              </div>
              <p>ड्राय-रन द्वारे दुबार नोंदी व त्रुटींची पडताळणी करून खात्री झाल्यावरच डेटाबेसमध्ये जतन करा.</p>
              {validationData && (
                <span style={{ fontSize: '0.8rem', color: validationData.errorCount > 0 ? '#b91c1c' : '#15803d', fontWeight: '700', marginTop: '0.25rem' }}>
                  {validationData.errorCount > 0 ? `⚠️ ${validationData.errorCount} त्रुटी सापडल्या` : '✓ सर्व नोंदी वैध आहेत'}
                </span>
              )}
            </div>
          </div>

          {/* File Upload Dropzone (if not yet successfully imported) */}
          {!importResult && (
            <div
              className={`gp-import-dropzone ${isDragging ? 'drag-active' : ''}`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx, .xls"
                style={{ display: 'none' }}
                onChange={(e) => e.target.files && handleFileSelect(e.target.files[0])}
              />
              <div className="gp-dropzone-icon">📁</div>
              <h4 className="gp-dropzone-title">
                {selectedFile ? 'दुसरी फाईल निवडण्यासाठी येथे क्लिक करा' : 'भरलेली एक्सेल (.xlsx) फाईल येथे ड्रॅग करा किंवा निवडा'}
              </h4>
              <p className="gp-dropzone-desc">कमाल फाईल मर्यादा: 15 MB | फक्त .xlsx किंवा .xls फाईल्स</p>
            </div>
          )}

          {/* Final Success Report Card */}
          {importResult && (
            <div className="gp-import-success-card">
              <div className="gp-import-success-header">
                <span style={{ fontSize: '1.75rem' }}>🎉</span>
                <div>
                  <h4>{importResult.message || 'नोंदी यशस्वीरित्या आयात झाल्या आहेत!'}</h4>
                  <p style={{ margin: '0.2rem 0 0', fontSize: '0.85rem', color: '#047857' }}>
                    सर्व खाती, मालमत्ता, नागरिक व कर मागण्या प्रणालीमध्ये जोडल्या गेल्या आहेत.
                  </p>
                </div>
              </div>
              {importResult.summary && (
                <div className="gp-success-metrics-grid">
                  <div className="gp-metric-box">
                    <span>एकूण प्रक्रिया केलेल्या नोंदी</span>
                    <strong>{importResult.summary.totalRowsProcessed}</strong>
                  </div>
                  <div className="gp-metric-box">
                    <span>तयार झालेली कुटुंबे (Households)</span>
                    <strong>{importResult.summary.householdsCreated}</strong>
                  </div>
                  <div className="gp-metric-box">
                    <span>तयार झालेल्या मालमत्ता (Properties)</span>
                    <strong>{importResult.summary.propertiesCreated}</strong>
                  </div>
                  <div className="gp-metric-box">
                    <span>तयार झालेली कर खाती (Tax Accounts)</span>
                    <strong>{importResult.summary.taxAccountsCreated}</strong>
                  </div>
                  <div className="gp-metric-box">
                    <span>निर्मित कर मागण्या (Demands)</span>
                    <strong>{importResult.summary.demandsCreated}</strong>
                  </div>
                  <div className="gp-metric-box">
                    <span>एकूण कर आकारणी रक्कम</span>
                    <strong>{money(importResult.summary.totalAssessedAmount)}</strong>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Preview / Validation Table */}
          {validationData && !importResult && (
            <div className="gp-import-preview-section">
              <div className="gp-import-preview-header">
                <div className="gp-preview-stats">
                  <span className="gp-stat-chip total">एकूण पंक्ती: {validationData.totalRows}</span>
                  <span className="gp-stat-chip valid">✓ वैध नोंदी: {validationData.validCount}</span>
                  {validationData.errorCount > 0 && (
                    <span className="gp-stat-chip invalid">⚠️ त्रुटी: {validationData.errorCount}</span>
                  )}
                </div>

                <div className="gp-filter-tabs">
                  <button
                    type="button"
                    className={`gp-filter-tab-btn ${filterTab === 'ALL' ? 'active' : ''}`}
                    onClick={() => setFilterTab('ALL')}
                  >
                    सर्व ({validationData.totalRows})
                  </button>
                  <button
                    type="button"
                    className={`gp-filter-tab-btn ${filterTab === 'VALID' ? 'active' : ''}`}
                    onClick={() => setFilterTab('VALID')}
                  >
                    वैध ({validationData.validCount})
                  </button>
                  <button
                    type="button"
                    className={`gp-filter-tab-btn ${filterTab === 'ERRORS' ? 'active' : ''}`}
                    onClick={() => setFilterTab('ERRORS')}
                  >
                    त्रुटी असलेल्या ({validationData.errorCount})
                  </button>
                </div>
              </div>

              <div className="gp-preview-table-container">
                <table className="gp-preview-table">
                  <thead>
                    <tr>
                      <th>पंक्ती</th>
                      <th>स्थिती</th>
                      <th>घर क्र.</th>
                      <th>करदात्याचे नाव</th>
                      <th>मोबाईल</th>
                      <th>प्रभाग</th>
                      <th>बांधकाम प्रकार</th>
                      <th>वापर प्रकार</th>
                      <th>एकूण कर (₹)</th>
                      <th>तपासणी शेरा / त्रुटी कारण</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row) => (
                      <tr key={row.rowNumber} className={row.isValid ? 'row-valid' : 'row-invalid'}>
                        <td>#{row.rowNumber}</td>
                        <td>
                          <span className={`gp-status-badge ${row.isValid ? 'valid' : 'invalid'}`}>
                            {row.isValid ? '✓ वैध' : '⚠️ त्रुटी'}
                          </span>
                        </td>
                        <td><strong>{row.ghar_kramank || '-'}</strong></td>
                        <td>{row.taxpayer_name || '-'}</td>
                        <td>{row.mobile_number || row.raw_mobile || '-'}</td>
                        <td>{row.ward_number || '-'}</td>
                        <td>{row.raw_construction || row.construction_type || '-'}</td>
                        <td>{row.raw_usage || row.usage_type || '-'}</td>
                        <td><strong>{money(row.total_all_taxes)}</strong></td>
                        <td>
                          {row.isValid ? (
                            <span style={{ color: '#16a34a', fontWeight: '600' }}>✓ नोंदणीसाठी तयार</span>
                          ) : (
                            <span className="gp-error-note">{row.errorSummary}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {filteredRows.length === 0 && (
                      <tr>
                        <td colSpan="10" style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                          या फिल्टर अंतर्गत कोणतीही नोंद सापडली नाही.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="gp-import-modal-footer">
          <div>
            {selectedFile && !importResult && (
              <button
                type="button"
                className="gp-import-action-btn secondary"
                onClick={resetAll}
                disabled={validating || importing}
              >
                🔄 फाईल बदला
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              type="button"
              className="gp-import-action-btn secondary"
              onClick={onClose}
              disabled={importing}
            >
              {importResult ? 'पूर्ण झाले (Close)' : 'रद्द करा'}
            </button>

            {!importResult && !validationData && (
              <button
                type="button"
                className="gp-import-action-btn primary"
                onClick={handleValidate}
                disabled={!selectedFile || validating}
              >
                {validating ? '⚡ तपासणी सुरू आहे…' : '🔍 पूर्वावलोकन व तपासणी (Validate)'}
              </button>
            )}

            {!importResult && validationData && (
              <button
                type="button"
                className="gp-import-action-btn primary"
                onClick={handleFinalImport}
                disabled={validationData.validCount === 0 || importing}
              >
                {importing ? '💾 आयात होत आहे…' : `✓ ${validationData.validCount} नोंदी अंतिम आयात करा (Confirm & Import)`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
